import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface ConorbitLoaderProps {
  /** "fullscreen" — fixed overlay covering the whole viewport (route transitions)
   *  "page"       — fills its nearest positioned parent (section-level)
   *  "inline"     — compact centred block inside a card / widget */
  variant?: "fullscreen" | "page" | "inline";
  /** Override the "Loading…" label */
  label?: string;
  className?: string;
}

export function ConorbitLoader({
  variant = "page",
  label = "Loading…",
  className,
}: ConorbitLoaderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const COLORS = ["#1B2E6B", "#2E7D32", "#C9962A"];
    const N = variant === "inline" ? 25 : 55;
    const MAX = variant === "inline" ? 80 : 130;
    const SPD = 0.4;

    let W = 0, H = 0;
    interface Node { x: number; y: number; vx: number; vy: number; r: number; color: string; }
    let nodes: Node[] = [];

    function resize() {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    }

    function mkNodes() {
      nodes = Array.from({ length: N }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * SPD,
        vy: (Math.random() - 0.5) * SPD,
        r: 2 + Math.random() * 3,
        color: COLORS[Math.floor(Math.random() * 3)],
      }));
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < MAX) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(100,120,160,${(1 - d / MAX) * 0.35})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.color + "99";
        ctx.fill();
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      }
      rafRef.current = requestAnimationFrame(draw);
    }

    resize();
    mkNodes();
    draw();

    const ro = new ResizeObserver(() => { resize(); mkNodes(); });
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [variant]);

  const svgSize = variant === "inline" ? 120 : 220;
  const svgHalf = svgSize / 2;
  const outerR = svgHalf * 0.95;
  const innerR = svgHalf * 0.66;
  const ballX = svgHalf + outerR;

  const wrapCls = cn(
    "flex items-center justify-center bg-white",
    variant === "fullscreen" && "fixed inset-0 z-[9999]",
    variant === "page"       && "absolute inset-0 z-10",
    variant === "inline"     && "relative w-full py-10",
    className,
  );

  return (
    <div className={wrapCls} role="status" aria-label={label}>
      {variant !== "inline" && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
        />
      )}

      <div className="relative z-10 flex flex-col items-center gap-5">
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer arc */}
          <g style={{ animation: "cl-cw 1.9s cubic-bezier(.6,0,.4,1) infinite", transformOrigin: `${svgHalf}px ${svgHalf}px` }}>
            <circle
              cx={svgHalf} cy={svgHalf} r={outerR}
              fill="none" stroke="#1B2E6B"
              strokeWidth={svgSize > 150 ? 7 : 4}
              strokeLinecap="round"
              strokeDasharray={`${outerR * 1.57} ${outerR * 4.5}`}
            />
          </g>
          {/* Orbiting ball */}
          <g style={{ animation: "cl-cw 2.5s linear infinite", transformOrigin: `${svgHalf}px ${svgHalf}px` }}>
            <circle cx={ballX} cy={svgHalf} r={svgSize > 150 ? 8 : 5} fill="#C9962A" />
          </g>
          {/* Inner arc */}
          <g style={{ animation: "cl-ccw 2.3s cubic-bezier(.6,0,.4,1) infinite", transformOrigin: `${svgHalf}px ${svgHalf}px` }}>
            <circle
              cx={svgHalf} cy={svgHalf} r={innerR}
              fill="none" stroke="#2E7D32"
              strokeWidth={svgSize > 150 ? 6 : 3}
              strokeLinecap="round"
              strokeDasharray={`${innerR * 1.7} ${innerR * 6.28}`}
            />
          </g>
          {/* Wordmark */}
          {variant !== "inline" && (
            <text
              x={svgHalf} y={svgHalf + 9}
              textAnchor="middle"
              fontFamily="sans-serif"
              fontSize={svgSize > 150 ? 18 : 11}
              fontWeight="900"
              letterSpacing="2"
            >
              <tspan fill="#1B2E6B">CON</tspan>
              <tspan fill="#2E7D32">O</tspan>
              <tspan fill="#1B2E6B">RBIT</tspan>
            </text>
          )}
        </svg>

        <div className="flex gap-2.5 items-center">
          {[0, 0.2, 0.4].map((delay, i) => (
            <div
              key={i}
              className="w-2.5 h-2.5 rounded-full"
              style={{
                background: ["#1B2E6B", "#2E7D32", "#C9962A"][i],
                animation: `cl-bounce 1.4s ease-in-out ${delay}s infinite`,
              }}
            />
          ))}
        </div>

        <p className="text-[11px] text-gray-400 tracking-[0.14em] uppercase font-bold font-sans">
          {label}
        </p>
      </div>

      <style>{`
        @keyframes cl-cw  { to { transform: rotate(360deg);  } }
        @keyframes cl-ccw { to { transform: rotate(-360deg); } }
        @keyframes cl-bounce {
          0%,80%,100% { transform: translateY(0);     opacity: .45; }
          40%          { transform: translateY(-8px); opacity: 1;   }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="cl-cw"],[style*="cl-ccw"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
